import { UNIT_SLUGS, emptyUnitMap, normalizeUnitMap } from "./unitDisplay";

export const GRID_SIZE = 10;
export const GATE_ROW = 0;
export const CELL_MAX = 100;
export const DEFAULT_GATE_COL = 4;

export function cloneCells(cells) {
  return (Array.isArray(cells) ? cells : []).map((cell) => ({
    row: cell.row,
    col: cell.col,
    unit_slug: cell.unit_slug,
    quantity: cell.quantity,
  }));
}

export function sortCells(cells) {
  return cloneCells(cells).sort((a, b) => a.row - b.row || a.col - b.col);
}

export function findCell(cells, row, col) {
  return (cells || []).find((cell) => cell.row === row && cell.col === col) || null;
}

export function withoutCell(cells, row, col) {
  return (cells || []).filter((cell) => !(cell.row === row && cell.col === col));
}

export function totalsBySlug(cells) {
  const totals = emptyUnitMap();
  for (const cell of cells || []) {
    const slug = String(cell.unit_slug || "").toLowerCase();
    if (!Object.prototype.hasOwnProperty.call(totals, slug)) continue;
    totals[slug] += Math.max(0, Math.floor(Number(cell.quantity) || 0));
  }
  return totals;
}

export function remainingBySlug(cells, stocks) {
  const owned = normalizeUnitMap(stocks);
  const used = totalsBySlug(cells);
  const remaining = emptyUnitMap();
  for (const slug of UNIT_SLUGS) {
    remaining[slug] = Math.max(0, owned[slug] - used[slug]);
  }
  return remaining;
}

export function sliderMaxForSlug(cells, stocks, slug) {
  const remaining = remainingBySlug(cells, stocks);
  const key = String(slug || "").toLowerCase();
  return Math.min(CELL_MAX, Math.max(0, remaining[key] || 0));
}

export function parseGateCol(value) {
  const n = Math.floor(Number(value));
  if (!Number.isFinite(n) || n < 0 || n >= GRID_SIZE) return DEFAULT_GATE_COL;
  return n;
}

export function isPlaceable(row, col, gateCol) {
  const r = Math.floor(Number(row));
  const c = Math.floor(Number(col));
  const gate = parseGateCol(gateCol);
  return (
    Number.isFinite(r) &&
    Number.isFinite(c) &&
    r >= 0 &&
    r < GRID_SIZE &&
    c >= 0 &&
    c < GRID_SIZE &&
    !(r === GATE_ROW && c === gate)
  );
}

/**
 * Select-then-click placement.
 * - Gate cell (row 0 + gate_col): no-op
 * - No selected slug: clear the cell
 * - Same slug / empty: add qty, clamped to 100 and remaining stock
 * - Different slug: replace (old units return to remaining)
 */
export function placeStack(cells, row, col, selectedSlug, placeQty, stocks, gateCol) {
  if (!isPlaceable(row, col, gateCol)) {
    return { cells: sortCells(cells), changed: false };
  }

  const current = findCell(cells, row, col);
  const qty = Math.max(0, Math.floor(Number(placeQty) || 0));
  const slug = selectedSlug ? String(selectedSlug).toLowerCase() : null;

  if (!slug) {
    if (!current) return { cells: sortCells(cells), changed: false };
    return { cells: sortCells(withoutCell(cells, row, col)), changed: true };
  }

  if (!UNIT_SLUGS.includes(slug)) {
    return { cells: sortCells(cells), changed: false };
  }

  if (current && current.unit_slug !== slug) {
    const without = withoutCell(cells, row, col);
    const remaining = remainingBySlug(without, stocks);
    const add = Math.min(qty, CELL_MAX, remaining[slug] || 0);
    if (add < 1) return { cells: sortCells(cells), changed: false };
    without.push({ row, col, unit_slug: slug, quantity: add });
    return { cells: sortCells(without), changed: true };
  }

  const currentQty =
    current && current.unit_slug === slug
      ? Math.max(0, Math.floor(Number(current.quantity) || 0))
      : 0;
  const remaining = remainingBySlug(cells, stocks);
  const add = Math.min(qty, CELL_MAX - currentQty, remaining[slug] || 0);
  if (add < 1) return { cells: sortCells(cells), changed: false };
  const next = withoutCell(cells, row, col);
  next.push({ row, col, unit_slug: slug, quantity: currentQty + add });
  return { cells: sortCells(next), changed: true };
}

export function parseCellsPayload(payload, gateCol) {
  const raw = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.cells)
    ? payload.cells
    : [];
  const seen = new Set();
  const cells = [];
  for (const entry of raw) {
    if (!entry || typeof entry !== "object") continue;
    const row = Math.floor(Number(entry.row));
    const col = Math.floor(Number(entry.col));
    const unitSlug = String(entry.unit_slug || "").toLowerCase();
    const quantity = Math.floor(Number(entry.quantity));
    if (!isPlaceable(row, col, gateCol)) continue;
    if (!UNIT_SLUGS.includes(unitSlug)) continue;
    if (!Number.isFinite(quantity) || quantity < 1 || quantity > CELL_MAX) continue;
    const key = `${row},${col}`;
    if (seen.has(key)) continue;
    seen.add(key);
    cells.push({ row, col, unit_slug: unitSlug, quantity });
  }
  return sortCells(cells);
}

export function parseGarrisonHero(input, cells, gateCol) {
  if (!input || typeof input !== "object") return null;
  const heroId = Math.floor(
    Number(input.heroId != null ? input.heroId : input.hero_id)
  );
  const row = Math.floor(Number(input.row));
  const col = Math.floor(Number(input.col));
  if (!Number.isFinite(heroId) || heroId < 1) return null;
  if (!isPlaceable(row, col, gateCol)) return null;
  if (findCell(cells, row, col)) return null;
  return { heroId, row, col };
}

export function parseFormationPayload(payload) {
  const gate_col = parseGateCol(
    payload && typeof payload === "object" && !Array.isArray(payload)
      ? payload.gate_col
      : undefined
  );
  const cells = parseCellsPayload(payload, gate_col);
  const garrisonHero = parseGarrisonHero(
    payload && typeof payload === "object" && !Array.isArray(payload)
      ? payload.garrisonHero
      : null,
    cells,
    gate_col
  );
  return {
    cells,
    gate_col,
    garrisonHero,
  };
}

export function gridCells() {
  const list = [];
  for (let row = 0; row < GRID_SIZE; row += 1) {
    for (let col = 0; col < GRID_SIZE; col += 1) {
      list.push({ row, col, isGate: row === GATE_ROW });
    }
  }
  return list;
}
